import {useState, useEffect, Dispatch} from 'react';
import {useHistory} from 'react-router-dom';
import {updateTenantById, freezeTenantById} from '../../api/tenants';
import ConfirmationPopup from '../common/popup/ConfirmationPopup';
import {Button, ButtonWithLoader} from '../common/Button';
import {Form, Formik, FormikHelpers} from 'formik';
import {setTitle} from '../../store/header/header.slice';
import {useAppDispatch} from '../../store/hooks';
import {sanitize} from '../../helpers';
import {handleApiValidationErrors} from '../../helpers/error';
import RequestError from '../../api/RequestError';
import ActionButtons from '../common/layout/ActionButtons';
import EnsurePermission from '../common/EnsurePermission';
import permissionsInfo from '../../constants/permissionsInfo';
import routes from './routes';
import {editTenantValidationSchema} from '../../constants/validationSchemes';
import {EditGroupProps} from '../../constants/types';
import useOnUnsavedChanges from '../../hooks/useOnUnsavedChanges';
import {useTranslation} from 'react-i18next';
import {FormGroup} from '@mui/material';
import {TextField} from '../common/formik/fields';
import {useSnackbarHook} from '../../hooks/useSnackbar';

const popupLabels = {
  body: 'The tenant will be frozen.'
};

type InformationProps = {
  id: string,
  name: string,
  setTenantName: Dispatch<string>
}

const Information = ({id, name, setTenantName}: InformationProps) => {
  const {t} = useTranslation('tenants');
  const dispatch = useAppDispatch();
  const history = useHistory();
  const {addSuccessToast, addErrorToast} = useSnackbarHook();

  useEffect(() => {
    dispatch(setTitle(''));
  }, []);

  const [isDeleting, setDeleting] = useState(false);
  const [apiValidationError, setApiValidationError] =
       useState<Nullable<Dictionary<string[]>>>(null);
  const [isPopupShown, setIsPopupShown] = useState(false);

  const clearMessages = () => {
    setApiValidationError(null);
  };

  const onSubmit = async (
      values: EditGroupProps,
      actions: FormikHelpers<EditGroupProps>
  ) => {
    const sanitizeValues = sanitize(values);

    try {
      await updateTenantById(id, sanitizeValues);
      setTenantName(sanitizeValues?.name);
      actions.resetForm({values: sanitizeValues});
      addSuccessToast(t('toasts.updateTenantMessage'));
    } catch (error) {
      if (error instanceof RequestError && error?.validationErrors) {
        const errors = handleApiValidationErrors(error, 'tenant');
        setApiValidationError(errors);
      } else {
        addErrorToast(error?.message);
      }
    }
  };

  const freezeTenant = async () => {
    setDeleting(true);

    try {
      await freezeTenantById(id);
      history.push(routes.tenants());
      addSuccessToast(t('toasts.freezeTenantMessage'));
    } catch (error) {
      addErrorToast(error?.message);
    } finally {
      setIsPopupShown(false);
      setDeleting(false);
    }
  };

  const initialValues = {name};

  return (
    <div className="tab-content">
      <Formik
        initialValues={initialValues}
        validationSchema={editTenantValidationSchema}
        validateOnBlur={false}
        onSubmit={onSubmit}
      >
        {({setFieldTouched, submitForm,
          isSubmitting, isValid, dirty}) => {
          useOnUnsavedChanges(dirty);

          return (
            <>
              <Form className="form">
                <FormGroup>
                  <TextField
                    label="Tenant Name"
                    name="name"
                    id="tenantName"
                    required
                    disabled={isSubmitting}
                    apiValidationError={apiValidationError}
                    onBlur={() => {
                      setFieldTouched('name', true);
                      clearMessages();
                    }}
                  />
                </FormGroup>
              </Form>

              <ActionButtons className="space-between">
                <EnsurePermission
                  elementPermissions={permissionsInfo.tenants.edit}>
                  <ButtonWithLoader
                    onClick={submitForm}
                    disabled={!(isValid && dirty)}
                    isSubmitting={isSubmitting}
                    testId="edit-tenant"
                    label="Update tenant"
                    type="submit"
                  />
                </EnsurePermission>

                <EnsurePermission
                  elementPermissions={permissionsInfo.tenants.freeze}>
                  <Button
                    onClick={() => {
                      setIsPopupShown(true);
                    }}
                    testId="freeze-tenant"
                    label="Freeze Tenant"
                    variant="text"
                    color="secondary"
                  />
                </EnsurePermission>
              </ActionButtons>
            </>
          );
        }}
      </Formik>

      <ConfirmationPopup
        labels={popupLabels}
        isOpen={isPopupShown}
        setIsOpen={setIsPopupShown}
        confirmableAction={freezeTenant}
        isActionProcessing={isDeleting}
      />
    </div>
  );
};

export default Information;
